const DEFAULT_FALLBACK_URL = '/maindashboard';
const SHIPMENT_FALLBACK_URL = '/shipmentmng';
const MANUFACTURING_FALLBACK_URL = '/manufacturingmng';
const SHIPMENT_PROCESS_AUTH = 'MANU_SHIPMENT_PROCESS';

const SHIPMENT_TARGETS = new Set(['SHIPMENT', 'SHIPMENT_CANDIDATE']);
const SHIPMENT_LINKS = new Set(['SHIPMENT_DETAIL', 'SHIPMENT_CANDIDATE_DETAIL']);
const MANUFACTURING_TARGETS = new Set(['MANUFACTURING_PLAN', 'MANUFACTURING_ROUTING', 'MANUFACTURING_QC']);
const MANUFACTURING_LINKS = new Set([
  'MANUFACTURING_PLAN_DETAIL',
  'MANUFACTURING_ROUTING',
  'MANUFACTURING_QC',
]);

export const safeInternalUrl = (url) => {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (/[\n\r\t]/.test(trimmed)) return null;
  return trimmed;
};

const normalizeCode = (value) => (value ? String(value).trim().toUpperCase() : '');

const routeFallbackFor = (noti) => {
  const targetType = normalizeCode(noti?.targetType);
  const linkKey = normalizeCode(noti?.linkKey);

  if (SHIPMENT_TARGETS.has(targetType) || SHIPMENT_LINKS.has(linkKey)) {
    return SHIPMENT_FALLBACK_URL;
  }
  if (MANUFACTURING_TARGETS.has(targetType) || MANUFACTURING_LINKS.has(linkKey)) {
    return MANUFACTURING_FALLBACK_URL;
  }
  return DEFAULT_FALLBACK_URL;
};

const hasUserAuth = (user, authTyp) => {
  const auths = user?.privilegeList || user?.authList || user?.auths || [];
  return auths.some((auth) => {
    const type = auth?.authTyp || auth?.AUTH_TYP;
    const enabled = auth?.authYn || auth?.AUTH_YN || 'Y';
    return type === authTyp && enabled !== 'N';
  });
};

const normalizeMenuPath = (link) => {
  if (typeof link !== 'string') return null;
  const trimmed = link.trim();
  if (!trimmed) return null;
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.replace(/\/+$/, '') || '/';
};

const collectMenuLinks = (menus, out = new Set()) => {
  if (!Array.isArray(menus)) return out;

  menus.forEach((menu) => {
    const path = normalizeMenuPath(menu?.link || menu?.menuLink);
    if (path) out.add(path);
    collectMenuLinks(menu?.items, out);
  });

  return out;
};

export const buildMenuLinkSet = (menus) => collectMenuLinks(menus);

const isMenuCheckUnavailable = (menuLinks) => menuLinks == null;

const hasMenuAccess = (url, menuLinks) => {
  if (isMenuCheckUnavailable(menuLinks)) return true;

  const safeUrl = safeInternalUrl(url);
  if (!safeUrl) return false;
  const normalizedUrl = normalizeMenuPath(safeUrl);
  const firstSegment = `/${normalizedUrl.split('/').filter(Boolean)[0] || ''}`;

  return menuLinks.has(normalizedUrl)
    || menuLinks.has(firstSegment)
    || [...menuLinks].some((link) => normalizedUrl === link || normalizedUrl.startsWith(`${link}/`));
};

const isShipmentNotification = (noti) => {
  const targetType = normalizeCode(noti?.targetType);
  const linkKey = normalizeCode(noti?.linkKey);
  return SHIPMENT_TARGETS.has(targetType) || SHIPMENT_LINKS.has(linkKey);
};

const accessibleFallback = (fallbackUrl, menuLinks) => {
  const candidates = [
    fallbackUrl,
    routeFallbackFor({ targetType: '', linkKey: '' }),
    DEFAULT_FALLBACK_URL,
    '/',
  ];

  return candidates.find((candidate) => {
    const safeUrl = safeInternalUrl(candidate);
    return safeUrl && hasMenuAccess(safeUrl, menuLinks);
  }) || '/';
};

export const resolveNotificationNavigateUrl = (noti, { user, menuLinks } = {}) => {
  const routeFallback = routeFallbackFor(noti);
  const fallbackUrl = safeInternalUrl(noti?.fallbackUrl) || routeFallback;
  const primaryUrl = safeInternalUrl(noti?.linkUrl) || safeInternalUrl(noti?.notiUrl) || fallbackUrl;

  if (isShipmentNotification(noti) && !hasUserAuth(user, SHIPMENT_PROCESS_AUTH)) {
    return accessibleFallback(fallbackUrl, menuLinks);
  }

  if (!hasMenuAccess(primaryUrl, menuLinks)) {
    return accessibleFallback(fallbackUrl, menuLinks);
  }

  return primaryUrl;
};
